import { useEffect, useState } from "react";
import { Plus, Trash2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { CLIENT_COLORS, SERVICE_TYPES, PRESTATIONS, legacyServices, CLIENT_INTERNAL_STATUSES, CONTACT_TYPES, PROFILE_MISSING_LABELS } from "@/lib/constants";
import { toast } from "sonner";

const F = ({ label, children, testId }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

export default function ClientFicheModal({ open, onClose, client, onSaved }) {
  const [f, setF] = useState({});
  const [billingSame, setBillingSame] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [tagsStr, setTagsStr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !client) return;
    setF({
      name: client.name || "", sector: client.sector || "", color: client.color || CLIENT_COLORS[0],
      email: client.email || "", phone: client.phone || "",
      contact_first_name: client.contact_first_name || "", contact_last_name: client.contact_last_name || "",
      contact_function: client.contact_function || "",
      address: client.address || "", address2: client.address2 || "",
      zip_code: client.zip_code || "", city: client.city || "", country: client.country || "France",
      service_type: client.service_type || "communication",
      services: client.services?.length ? [...client.services] : legacyServices(client.service_type),
      status: client.status || "actif",
      source: client.source || "", responsible: client.responsible || "",
      internal_notes: client.internal_notes || "",
      validation_mode: client.validation_rule?.mode || "one",
      validation_count: client.validation_rule?.count || 2,
      objective_week: client.objective_week ?? 0, objective_month: client.objective_month ?? 0,
      billing_email: client.billing_email || "", billing_contact: client.billing_contact || "",
      billing_address: client.billing_address || "", billing_siret: client.billing_siret || "",
      vat_number: client.vat_number || "",
    });
    setBillingSame(!(client.billing_address || "").trim() && !(client.billing_siret || "").trim());
    setContacts(Array.isArray(client.contacts) ? client.contacts.map((c) => ({ ...c })) : []);
    setTagsStr((client.tags || []).join(", "));
  }, [open, client]);

  if (!client) return null;
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const missing = client.profile_missing || [];

  const setContact = (i, k, v) =>
    setContacts((cs) => cs.map((c, j) => (j === i ? { ...c, [k]: v } : c)));

  const mainAddress = [f.address, f.address2, [f.zip_code, f.city].filter(Boolean).join(" "), f.country]
    .filter(Boolean).join(", ");

  const submit = async () => {
    if (!f.name.trim()) { toast.error("Le nom du client est requis"); return; }
    setLoading(true);
    const payload = {
      ...f,
      objective_week: Number(f.objective_week) || 0,
      objective_month: Number(f.objective_month) || 0,
      tags: tagsStr.split(",").map((t) => t.trim()).filter(Boolean),
      contacts,
      validation_rule: { mode: f.validation_mode, count: Number(f.validation_count) || 1 },
    };
    if (billingSame) {
      payload.billing_email = f.email;
      payload.billing_contact = [f.contact_first_name, f.contact_last_name].filter(Boolean).join(" ") || f.name;
      payload.billing_address = mainAddress;
    }
    try {
      await api.patch(`/clients/${client.id}`, payload);
      toast.success("Fiche client enregistrée");
      window.dispatchEvent(new Event("sf:clients"));
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally { setLoading(false); }
  };

  const sel = "mt-1 w-full text-sm border border-neutral-200 rounded-md px-2.5 py-2 bg-white";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-settings-modal" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading">Fiche client — {client.name}</DialogTitle></DialogHeader>

        {missing.length > 0 && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 text-xs" data-testid="fiche-missing-warning">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>Fiche client à compléter — manquant pour les devis/factures : {missing.map((m) => PROFILE_MISSING_LABELS[m] || m).join(", ")}.</span>
          </div>
        )}

        <div className="space-y-5">
          <section>
            <h3 className="font-heading font-bold text-sm mb-2">Identité</h3>
            <div className="grid grid-cols-2 gap-3">
              <F label="Nom du client / entreprise *"><Input data-testid="settings-client-name" value={f.name} onChange={set("name")} /></F>
              <F label="Domaine d'activité"><Input data-testid="settings-client-sector" value={f.sector} onChange={set("sector")} /></F>
              <F label="Prénom du contact"><Input data-testid="fiche-contact-first" value={f.contact_first_name} onChange={set("contact_first_name")} /></F>
              <F label="Nom du contact"><Input data-testid="fiche-contact-last" value={f.contact_last_name} onChange={set("contact_last_name")} /></F>
              <F label="Fonction"><Input data-testid="fiche-contact-function" value={f.contact_function} onChange={set("contact_function")} placeholder="Ex : Gérante" /></F>
              <F label="Email"><Input data-testid="settings-client-email" type="email" value={f.email} onChange={set("email")} /></F>
              <F label="Téléphone"><Input data-testid="fiche-phone" value={f.phone} onChange={set("phone")} /></F>
              <F label="Espaces dédiés — le client apparaît dans les modules cochés">
                <div className="grid grid-cols-2 gap-1.5" data-testid="fiche-services">
                  {PRESTATIONS.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`fiche-service-${p.id}`}>
                      <input type="checkbox" className="rounded" checked={(f.services || []).includes(p.id)}
                        onChange={(e) => setF((prev) => {
                          const s = prev.services || [];
                          return { ...prev, services: e.target.checked ? [...s, p.id] : s.filter((x) => x !== p.id) };
                        })} />
                      {p.label}
                    </label>
                  ))}
                </div>
              </F>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <F label="Adresse"><Input data-testid="fiche-address" value={f.address} onChange={set("address")} /></F>
              <F label="Complément"><Input data-testid="fiche-address2" value={f.address2} onChange={set("address2")} /></F>
              <F label="Code postal"><Input data-testid="fiche-zip" value={f.zip_code} onChange={set("zip_code")} /></F>
              <F label="Ville"><Input data-testid="fiche-city" value={f.city} onChange={set("city")} /></F>
              <F label="Pays"><Input data-testid="fiche-country" value={f.country} onChange={set("country")} /></F>
            </div>
          </section>

          <section className="pt-4 border-t border-neutral-100">
            <h3 className="font-heading font-bold text-sm mb-2">Coordonnées de facturation</h3>
            <label className="flex items-center gap-2 text-sm mb-3 cursor-pointer" data-testid="billing-same-as-main">
              <input type="checkbox" checked={billingSame} onChange={(e) => setBillingSame(e.target.checked)} className="rounded" />
              Utiliser les coordonnées principales
            </label>
            {!billingSame && (
              <div className="grid grid-cols-2 gap-3">
                <F label="Email de facturation"><Input data-testid="fiche-billing-email" type="email" value={f.billing_email} onChange={set("billing_email")} /></F>
                <F label="Contact facturation"><Input data-testid="fiche-billing-contact" value={f.billing_contact} onChange={set("billing_contact")} /></F>
                <div className="col-span-2"><F label="Adresse de facturation"><Input data-testid="fiche-billing-address" value={f.billing_address} onChange={set("billing_address")} /></F></div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 mt-3">
              <F label="SIREN / SIRET"><Input data-testid="fiche-billing-siret" value={f.billing_siret} onChange={set("billing_siret")} /></F>
              <F label="N° TVA intracommunautaire"><Input data-testid="fiche-vat" value={f.vat_number} onChange={set("vat_number")} /></F>
              <F label="Pays">
                <select data-testid="fiche-country" className={sel} value={f.country || "France"} onChange={set("country")}>
                  <option value="France">France</option>
                  <option value="Suisse">Suisse</option>
                  <option value="Belgique">Belgique</option>
                  <option value="Autre">Autre</option>
                </select>
              </F>
              {f.country === "Suisse" && (
                <F label="N° IDE / CHE (Suisse)"><Input data-testid="fiche-ide" value={f.ide_che || ""} onChange={set("ide_che")} placeholder="CHE-000.000.000" /></F>
              )}
              <F label="Nom commercial (si différent)"><Input data-testid="fiche-trade-name" value={f.trade_name || ""} onChange={set("trade_name")} /></F>
              <F label="Tél. responsable facturation"><Input data-testid="fiche-billing-phone" value={f.billing_phone || ""} onChange={set("billing_phone")} /></F>
            </div>
          </section>

          <section className="pt-4 border-t border-neutral-100">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading font-bold text-sm">Contacts</h3>
              <Button size="sm" variant="outline" data-testid="fiche-contact-add"
                onClick={() => setContacts((cs) => [...cs, { first_name: "", last_name: "", function: "", email: "", phone: "", type: "autre" }])}
                className="gap-1 text-xs">
                <Plus className="w-3.5 h-3.5" /> Ajouter un contact
              </Button>
            </div>
            {contacts.length === 0 ? (
              <p className="text-xs text-neutral-400" data-testid="fiche-contacts-empty">Aucun contact additionnel.</p>
            ) : (
              <div className="space-y-3" data-testid="fiche-contacts">
                {contacts.map((c, i) => (
                  <div key={c.id || i} className="border border-neutral-200 rounded-lg p-3 space-y-2" data-testid={`fiche-contact-${i}`}>
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Prénom" value={c.first_name} onChange={(e) => setContact(i, "first_name", e.target.value)} data-testid={`fiche-contact-${i}-first`} />
                      <Input placeholder="Nom" value={c.last_name} onChange={(e) => setContact(i, "last_name", e.target.value)} data-testid={`fiche-contact-${i}-last`} />
                      <Input placeholder="Fonction" value={c.function} onChange={(e) => setContact(i, "function", e.target.value)} data-testid={`fiche-contact-${i}-function`} />
                      <select className="text-sm border border-neutral-200 rounded-md px-2.5 py-2 bg-white" value={c.type}
                        onChange={(e) => setContact(i, "type", e.target.value)} data-testid={`fiche-contact-${i}-type`}>
                        {CONTACT_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                      </select>
                      <Input placeholder="Email" type="email" value={c.email} onChange={(e) => setContact(i, "email", e.target.value)} data-testid={`fiche-contact-${i}-email`} />
                      <div className="flex gap-2">
                        <Input placeholder="Téléphone" value={c.phone} onChange={(e) => setContact(i, "phone", e.target.value)} data-testid={`fiche-contact-${i}-phone`} />
                        <button onClick={() => setContacts((cs) => cs.filter((_, j) => j !== i))}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-md transition-colors" data-testid={`fiche-contact-${i}-remove`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="pt-4 border-t border-neutral-100">
            <h3 className="font-heading font-bold text-sm mb-2">Informations internes <span className="text-xs font-normal text-neutral-400">(visibles uniquement dans DGA)</span></h3>
            <div className="grid grid-cols-2 gap-3">
              <F label="Statut">
                <select data-testid="fiche-status" className={sel} value={f.status} onChange={set("status")}>
                  {CLIENT_INTERNAL_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </F>
              <F label="Règle de validation par défaut (créations)">
                <select data-testid="fiche-validation-rule" className={sel} value={f.validation_mode}
                  onChange={(e) => setF((p) => ({ ...p, validation_mode: e.target.value }))}>
                  <option value="one">1 validation suffit</option>
                  <option value="all">Tous les décideurs doivent valider</option>
                  <option value="count">X validations sur Y nécessaires</option>
                </select>
              </F>
              {f.validation_mode === "count" && (
                <F label="Nombre de validations requises">
                  <Input data-testid="fiche-validation-count" type="number" min="1" value={f.validation_count}
                    onChange={(e) => setF((p) => ({ ...p, validation_count: e.target.value }))} />
                </F>
              )}
              <F label="Responsable"><Input data-testid="fiche-responsible" value={f.responsible} onChange={set("responsible")} /></F>
              <F label="Source du client"><Input data-testid="fiche-source" value={f.source} onChange={set("source")} placeholder="Ex : Recommandation, Instagram…" /></F>
              <F label="Tags (séparés par des virgules)"><Input data-testid="fiche-tags" value={tagsStr} onChange={(e) => setTagsStr(e.target.value)} placeholder="Ex : VIP, fidèle" /></F>
            </div>
            <div className="mt-3">
              <F label="Notes internes"><Textarea data-testid="fiche-internal-notes" rows={3} value={f.internal_notes} onChange={set("internal_notes")} /></F>
            </div>
          </section>

          <section className="pt-4 border-t border-neutral-100">
            <h3 className="font-heading font-bold text-sm mb-2">Communication digitale</h3>
            <div>
              <Label className="text-xs">Couleur de la marque</Label>
              <div className="flex gap-2 mt-2">
                {CLIENT_COLORS.map((c) => (
                  <button key={c} data-testid={`settings-color-${c}`} onClick={() => setF((p) => ({ ...p, color: c }))}
                    className={`w-8 h-8 rounded-full transition-transform ${f.color === c ? "ring-2 ring-offset-2 ring-neutral-900 scale-110" : "hover:scale-105"}`}
                    style={{ background: c }} />
                ))}
              </div>
            </div>
          </section>

          <Button data-testid="settings-client-save" onClick={submit} disabled={loading || !(f.name || "").trim()}
            className="w-full bg-neutral-900 hover:bg-neutral-800 text-white">
            {loading ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
